import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Typography, Table, Switch, message, Tag, Button, Input, Select, Row, Col,
  Space, Spin, Popconfirm, Divider, Tooltip,
} from 'antd';
import { DeleteOutlined, PlusOutlined, MailOutlined, TeamOutlined } from '@ant-design/icons';
import api from '../api';
import { UserContext } from '../App';

// What each alert is about, taken from its key. Prefix first, so a key added
// later lands somewhere sensible without being listed here, with explicit
// entries only where the prefix would be wrong.
//
// rescore_requested has no prefix and covers evaluations and error
// submissions both. It is filed under Evaluation because that is where the
// request is raised, and the alert's own name says the rest.
const ALERT_OBJECTS = {
  coaching:   'Coaching',
  eval:       'Evaluation',
  feedback:   'Evaluation',
  error:      'Error',
  compliance: 'Compliance',
  writeup:    'Formal Writeup',
  absence:    'Absence',
  idea:       'Idea',
  variance:   'Schedule Variance',
  helpdesk:   'Help Desk',
};
const ALERT_OBJECT_OVERRIDES = {
  rescore_requested: 'Evaluation',
};

function alertObject(key) {
  const k = String(key || '');
  if (ALERT_OBJECT_OVERRIDES[k]) return ALERT_OBJECT_OVERRIDES[k];
  const prefix = k.split('_')[0];
  if (ALERT_OBJECTS[prefix]) return ALERT_OBJECTS[prefix];
  // Unknown prefix: show it rather than a blank, so a new key reads as
  // unclassified instead of looking like it belongs nowhere.
  return prefix ? prefix.charAt(0).toUpperCase() + prefix.slice(1) : '—';
}

const PLACEHOLDERS_ABSENCE  = 'Absence: {{agent_name}}  {{team_name}}  {{absence_type}}  {{date}}  {{date_end}}  {{time_from}}  {{time_to}}  {{status}}  {{submitted_by}}  {{notes}}  {{link}}   HR alert only: {{action}}  {{changes}}';
const PLACEHOLDERS_COACHING = 'Coaching: {{session_title}}  {{session_id}}  {{request_type}}  {{campaign}}  {{due_date}}  {{assigned_by}}  {{commented_by}}  {{comment}}  {{link}}';
const PLACEHOLDERS_IDEAS    = 'Ideas: {{idea_title}}  {{submitter_name}}  {{team_name}}  {{status}}  {{updated_by}}  {{link}}';
const PLACEHOLDERS_EVAL     = 'Evaluations: {{agent_name}}  {{call_date}}  {{campaign}}  {{form_name}}  {{evaluator}}  {{score}}  {{acknowledge_by}}  {{evaluation_id}}  {{dispute_reason}}  {{changed_by}}  {{commented_by}}  {{comment}}  {{link}}';
const PLACEHOLDERS_ERROR    = 'Errors: {{agent_name}}  {{date_of_error}}  {{created_date}}  {{campaign}}  {{form_name}}  {{error_id}}  {{dispute_reason}}  {{changed_by}}  {{commented_by}}  {{comment}}  {{link}}';
const PLACEHOLDERS_COMPL    = 'Compliance: {{employee_name}}  {{date}}  {{type}}  {{incident_id}}  {{changed_by}}  {{link}}';
const PLACEHOLDERS_VARIANCE = 'Schedule Variance: {{action}}  {{first_name}}  {{employee_name}}  {{team_name}}  {{variance_type}}  {{variance_date}}  {{shift_start}}  {{shift_end}}  {{billable_ot_hours}}  {{remove_points}}  {{status}}  {{notes}}  {{billing_notes}}  {{reporter}}  {{changed_by}}  {{variance_id}}  {{link}}';
const PLACEHOLDERS_HELPDESK = 'Help Desk: {{ticket_id}}  {{ticket_subject}}  {{request_type}}  {{queue}}  {{status}}  {{status_change}}  {{impacted_employee}}  {{client}}  {{client_contact}}  {{campaign}}  {{changed_by}}  {{comment}}  {{public_comment_log}}  {{link}}';
const PLACEHOLDERS_ACKDUE   = 'Acknowledgements due today: {{agent_name}}  {{due_date}}  {{item_count}}  {{items}}  {{link}}';

function RecipientPanel({ record, isAdmin }) {
  const [recipients, setRecipients] = useState(null);
  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [addTeam, setAddTeam] = useState(null);
  const [addType, setAddType] = useState('user');
  const [addUser, setAddUser] = useState(null);
  const [addEmail, setAddEmail] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, tRes, uRes] = await Promise.all([
        api.get(`/api/alert-configs/${record.ID}/recipients`),
        api.get('/api/teams'),
        api.get('/api/users'),
      ]);
      setRecipients(rRes.data);
      setTeams(tRes.data || []);
      setUsers(uRes.data || []);
    } catch {
      message.error('Failed to load recipients.');
    } finally {
      setLoading(false);
    }
  }, [record.ID]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (rid) => {
    try {
      await api.delete(`/api/alert-configs/${record.ID}/recipients/${rid}`);
      setRecipients((p) => p.filter((r) => r.ID !== rid));
    } catch {
      message.error('Failed to remove recipient.');
    }
  };

  const handleAdd = async () => {
    if (addType === 'user' && !addUser) { message.warning('Select a user.'); return; }
    if (addType === 'email' && !addEmail.trim()) { message.warning('Enter an email address.'); return; }
    setAdding(true);
    try {
      const payload = { team_id: addTeam || null };
      if (addType === 'user') payload.recipient_user_id = addUser;
      else payload.recipient_email = addEmail.trim();
      await api.post(`/api/alert-configs/${record.ID}/recipients`, payload);
      setAddUser(null); setAddEmail(''); setAddTeam(null);
      await load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to add recipient.');
    } finally {
      setAdding(false);
    }
  };

  const columns = [
    {
      title: 'Team scope',
      dataIndex: 'TeamName',
      key: 'TeamName',
      render: (v) => v || <Tag>All teams</Tag>,
    },
    {
      title: 'Recipient',
      key: 'Recipient',
      render: (_, r) => r.RecipientUserName
        ? <span>{r.RecipientUserName}</span>
        : <span style={{ fontFamily: 'monospace' }}>{r.RecipientEmail}</span>,
    },
    {
      title: 'Type',
      key: 'Type',
      width: 90,
      render: (_, r) => r.RecipientUserID ? <Tag color="blue">User</Tag> : <Tag color="purple">Email</Tag>,
    },
    isAdmin ? {
      title: '',
      key: 'del',
      width: 40,
      render: (_, r) => (
        <Popconfirm title="Remove this recipient?" onConfirm={() => handleDelete(r.ID)} okText="Remove" okType="danger">
          <Button size="small" type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    } : null,
  ].filter(Boolean);

  if (loading || recipients === null) return <Spin size="small" style={{ margin: 8 }} />;

  return (
    <div style={{ paddingTop: 4 }}>
      <Typography.Text strong style={{ fontSize: 13 }}>Email Recipients</Typography.Text>
      <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
        (recipients receive email when <MailOutlined /> Email is enabled above)
      </Typography.Text>
      <Table
        rowKey="ID"
        dataSource={recipients}
        columns={columns}
        size="small"
        pagination={false}
        style={{ marginTop: 8, marginBottom: isAdmin ? 12 : 0 }}
        locale={{ emptyText: 'No recipients configured.' }}
      />
      {isAdmin && (
        <Row gutter={8} align="middle" wrap={false}>
          <Col>
            <Select
              placeholder="All teams"
              allowClear
              style={{ width: 160 }}
              value={addTeam}
              onChange={setAddTeam}
              options={teams.map((t) => ({ value: t.ID, label: t.Name }))}
              size="small"
            />
          </Col>
          <Col>
            <Select
              value={addType}
              onChange={(v) => { setAddType(v); setAddUser(null); setAddEmail(''); }}
              style={{ width: 100 }}
              size="small"
              options={[
                { value: 'user', label: 'User' },
                { value: 'email', label: 'Email' },
              ]}
            />
          </Col>
          <Col flex="auto">
            {addType === 'user' ? (
              <Select
                showSearch
                placeholder="Select user"
                style={{ width: '100%' }}
                value={addUser}
                onChange={setAddUser}
                size="small"
                filterOption={(input, opt) => (opt?.label || '').toLowerCase().includes(input.toLowerCase())}
                options={users.map((u) => ({ value: u.ID, label: u.DisplayName || u.Email }))}
              />
            ) : (
              <Input
                placeholder="group@superiorcontact.net"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                size="small"
              />
            )}
          </Col>
          <Col>
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              loading={adding}
              onClick={handleAdd}
            >
              Add
            </Button>
          </Col>
        </Row>
      )}
    </div>
  );
}

function EmailTemplatePanel({ record, isAdmin, onSave }) {
  const [subject, setSubject] = useState(record.EmailSubject || '');
  const [body,    setBody]    = useState(record.EmailBodyTemplate || '');
  const [saving,  setSaving]  = useState(false);

  const savedSubject = record.EmailSubject || '';
  const savedBody    = record.EmailBodyTemplate || '';
  const dirty = subject !== savedSubject || body !== savedBody;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/api/alert-configs/${record.ID}`, {
        email_subject:        subject,
        email_body_template:  body,
      });
      record.EmailSubject       = subject;
      record.EmailBodyTemplate  = body;
      message.success('Template saved.');
    } catch {
      message.error('Failed to save template.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <Row gutter={16}>
        <Col span={24} style={{ marginBottom: 8 }}>
          <Typography.Text strong style={{ fontSize: 13 }}>Email Subject</Typography.Text>
          <Input
            style={{ marginTop: 4 }}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={!isAdmin}
            placeholder="Absence Notice -- {{agent_name}} on {{date}}"
            size="small"
          />
        </Col>
        <Col span={24} style={{ marginBottom: 4 }}>
          <Typography.Text strong style={{ fontSize: 13 }}>Email Body</Typography.Text>
          <Input.TextArea
            style={{ marginTop: 4, fontFamily: 'monospace', fontSize: 12 }}
            rows={5}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={!isAdmin}
            placeholder="Enter plain text. Line breaks are preserved."
          />
        </Col>
        <Col span={24}>
          <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
            <strong>Placeholders —</strong>
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{PLACEHOLDERS_ABSENCE}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{PLACEHOLDERS_COACHING}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{PLACEHOLDERS_IDEAS}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{PLACEHOLDERS_EVAL}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{PLACEHOLDERS_ERROR}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{PLACEHOLDERS_COMPL}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{PLACEHOLDERS_ACKDUE}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{PLACEHOLDERS_VARIANCE}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block' }}>{PLACEHOLDERS_HELPDESK}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
            A placeholder not listed for that alert&apos;s type arrives in the email as the literal text you typed,
            so only use the row that matches. {'{{link}}'} works on every alert.
          </Typography.Text>
        </Col>
        {isAdmin && (
          <Col span={24} style={{ marginTop: 10 }}>
            <Space align="center">
              <Button type="primary" size="small" loading={saving} onClick={save}>
                Save Template
              </Button>
              {dirty && (
                <Typography.Text type="warning" style={{ fontSize: 12 }}>
                  Unsaved changes
                </Typography.Text>
              )}
            </Space>
          </Col>
        )}
      </Row>
      <Divider style={{ margin: '12px 0' }} />
      <RecipientPanel record={record} isAdmin={isAdmin} />
    </div>
  );
}

export default function AlertConfigPage() {
  const currentUser = useContext(UserContext);
  const isAdmin     = currentUser?.Role === 'admin' || currentUser?.Role === 'qa_admin';
  const [configs, setConfigs]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [toggling, setToggling] = useState({});
  const [expanded, setExpanded] = useState([]);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/alert-configs');
      setConfigs(res.data);
    } catch {
      message.error('Failed to load alert configurations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const handleToggle = async (record, field, checked) => {
    const key = `${record.ID}_${field}`;
    setToggling((p) => ({ ...p, [key]: true }));
    const payload = field === 'is_active'
      ? { is_active: checked }
      : field === 'group_email'
        ? { group_email: checked }
        : { send_email: checked };
    const stateKey = field === 'is_active' ? 'IsActive' : field === 'group_email' ? 'GroupEmail' : 'SendEmail';
    try {
      await api.patch(`/api/alert-configs/${record.ID}`, payload);
      setConfigs((prev) =>
        prev.map((c) => c.ID === record.ID
          ? { ...c, [stateKey]: checked ? 1 : 0 }
          : c
        )
      );
      if (field === 'send_email' && checked && !expanded.includes(record.ID)) {
        setExpanded((p) => [...p, record.ID]);
      }
    } catch {
      message.error('Failed to update.');
    } finally {
      setToggling((p) => ({ ...p, [key]: false }));
    }
  };

  const columns = [
    {
      title: 'Object',
      key: 'Object',
      width: 130,
      // Sorted, so the coaching alerts sit together and the evaluation ones
      // together. The names do not group on their own: most begin "Alert
      // agent when ..." and the thing being alerted about is the last word.
      sorter: (a, b) => alertObject(a.Key).localeCompare(alertObject(b.Key)),
      render: (_, r) => <Tag>{alertObject(r.Key)}</Tag>,
    },
    {
      title: 'Alert',
      dataIndex: 'Name',
      key: 'Name',
      sorter: (a, b) => (a.Name || '').localeCompare(b.Name || ''),
    },
    {
      title: 'Status',
      key: 'Status',
      width: 90,
      render: (_, r) => (
        <Tag color={r.IsActive ? 'green' : 'default'}>{r.IsActive ? 'Active' : 'Inactive'}</Tag>
      ),
    },
    {
      title: 'Enabled',
      key: 'Enabled',
      width: 80,
      render: (_, r) => (
        <Switch
          checked={!!r.IsActive}
          loading={!!toggling[`${r.ID}_is_active`]}
          disabled={!isAdmin}
          onChange={isAdmin ? (checked) => handleToggle(r, 'is_active', checked) : undefined}
        />
      ),
    },
    {
      title: <Space><MailOutlined />Email</Space>,
      key: 'Email',
      width: 80,
      render: (_, r) => (
        <Switch
          checked={!!r.SendEmail}
          loading={!!toggling[`${r.ID}_send_email`]}
          disabled={!isAdmin}
          onChange={isAdmin ? (checked) => handleToggle(r, 'send_email', checked) : undefined}
        />
      ),
    },
    {
      title: (
        <Tooltip title="Send one combined email to all recipients so they can reply-all">
          <Space><TeamOutlined />Group</Space>
        </Tooltip>
      ),
      key: 'GroupEmail',
      width: 90,
      render: (_, r) => (
        <Tooltip title={!r.SendEmail ? 'Enable Email first' : 'One email to all recipients'}>
          <Switch
            checked={!!r.GroupEmail}
            loading={!!toggling[`${r.ID}_group_email`]}
            disabled={!isAdmin || !r.SendEmail}
            onChange={isAdmin && r.SendEmail ? (checked) => handleToggle(r, 'group_email', checked) : undefined}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <Typography.Title level={4} style={{ marginTop: 0, marginBottom: 4 }}>
        Alert Configuration
      </Typography.Title>
      <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Enable or disable system alerts and email notifications. Toggle <MailOutlined /> Email to expand template and recipient settings.
      </Typography.Text>

      <Table
        rowKey="ID"
        dataSource={configs}
        columns={columns}
        loading={loading}
        size="small"
        pagination={false}
        expandable={{
          expandedRowKeys: expanded,
          onExpandedRowsChange: setExpanded,
          expandedRowRender: (record) => (
            <div style={{ padding: '8px 24px 4px' }}>
              <EmailTemplatePanel record={record} isAdmin={isAdmin} />
            </div>
          ),
          rowExpandable: () => true,
        }}
      />
    </>
  );
}
